import { Injectable } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Router } from '@angular/router';
import { Observable, filter, switchMap, tap, Subject, catchError, finalize, EMPTY } from 'rxjs';
import { Payable } from '../../models/entities/payable';
import { PayableAction } from '../../enums/payables/payable-actions.enum';
import { PayableState } from '../../enums/payables/payable-states.enum';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { PayablesService } from './payables.service';
import { GrowlerService } from '../../components/shared/growler/growler.service';
import { PayableFulfillmentType } from '../../models/entities/payable-fulfillment';
import { MatDialogSizes } from '../../enums/mat-dialog-sizes.enum';
import { ViewInvoiceDialogComponent } from '../../components/app-layout/accounts-payable/payables-dashboard/view-invoice-dialog/view-invoice-dialog.component';
import { CreatePayableDialogStateService } from '../../components/app-layout/accounts-payable/create-payable-dialog/create-payable-dialog-state.service';
import { PayableUpdateDialogStateService } from '../../components/app-layout/accounts-payable/payable-update-dialog/payable-update-dialog-state.service';
import { PayInvoiceDialogComponent } from '../../components/app-layout/accounts-payable/payables-dashboard/pay-invoice-dialog/pay-invoice-dialog.component';
import { CheckPrintingDialogStateService } from '../../components/app-layout/accounts-payable/check-printing/create-check-dialog/check-printing-dialog-state.service';
import { DownloadCheckDialogStateService } from '../../components/app-layout/accounts-payable/check-printing/download-check-dialog/download-check-dialog-state.service';
import { TrackMailDialogComponent } from '../../components/app-layout/accounts-payable/payables-dashboard/track-mail-dialog/track-mail-dialog.component';
import { MarkAsPaidDialogComponent } from '../../components/app-layout/accounts-payable/payables-dashboard/mark-as-paid-dialog/mark-as-paid-dialog.component';
import { ConfirmActionDialogComponent } from '../../components/app-layout/shared/confirm-action-dialog/confirm-action-dialog.component';
import { CancelPaymentDialogComponent } from '../../components/app-layout/accounts-payable/payables-dashboard/cancel-payment-dialog/cancel-payment-dialog.component';
import {
    EntityHistoryDialogComponent,
    EntityHistoryDialogInput,
} from '../../components/v2-layout/shared/entity-history/entity-history-dialog.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PayableApproval } from '../../models/entities/payable-approvals';
import { ApprovePayableDialogComponent } from '../../components/app-layout/accounts-payable/payables-dashboard/approve-payable-dialog/approve-payable-dialog.component';
import { MatDialog as MatMdcDialog } from '@angular/material/dialog';
import { Routes } from '../../config/routes';
import moment from 'moment-timezone';

export interface PayableActionConfig {
    label: PayableAction;
    icon?: string;
    iconClass?: string;
    textClass?: string;
    alwaysVisible?: boolean;
    condition?: (payable: Payable) => boolean;
    action: (payable: Payable) => Observable<any> | void;
}

@UntilDestroy()
@Injectable({
    providedIn: 'root'
})
export class PayableActionsService {
    private readonly actionWorkingSubject$ = new Subject<void>();
    private readonly refreshAfterActionSubject$ = new Subject<void>();
    private readonly actionDoneSubject$ = new Subject<void>();

    private readonly _allActions: PayableActionConfig[] = [
        {
            label: PayableAction.VIEW,
            icon: 'eye',
            condition: () => true,
            action: (p) => this.viewPayableDetails(p)
        },
        {
            label: PayableAction.CONFIRM,
            icon: 'check',
            condition: (p) => this.hasWriteAccess() &&
                p.status === PayableState.DRAFT &&
                !p.deletedAt,
            action: (p) => this.openCreatePayableDialog(p)
        },
        {
            label: PayableAction.EDIT,
            icon: 'pencil',
            condition: (p) => this.hasWriteAccess() &&
                (p.currentFulfillment || p.status === PayableState.DRAFT) &&
                p.currentFulfillment?.status !== PayableState.APPROVED &&
                p.currentFulfillment?.status !== PayableState.PENDING_APPROVAL &&
                !p.deletedAt,
            action: (p) => this.openUpdatePayableDialog(p)
        },
        {
            label: PayableAction.SEND_PAYMENT,
            icon: 'money',
            iconClass: 'icon-send-payment',
            condition: (p) => this.hasWriteAccess() &&
                p.currentFulfillment?.status === PayableState.APPROVED &&
                !p.deletedAt,
            action: (p) => this.schedulePayment(p)
        },
        {
            label: PayableAction.PRINT_CHECK,
            icon: 'print',
            iconClass: 'icon-print-check',
            condition: (p) => this.hasWriteAccess() &&
                p.allowedTransitions.includes(PayableState.CHECK_PRINTED) &&
                !p.deletedAt,
            action: (p) => this.openPrintCheckDialog(p)
        },
        {
            label: PayableAction.MARK_AS_PAID,
            icon: 'check-circle',
            iconClass: 'icon-mark-as-paid',
            condition: (p) => this.hasWriteAccess() &&
                p.allowedTransitions.includes(PayableState.PAYMENT_MARKED_AS_PAID) &&
                !p.deletedAt,
            action: (p) => this.openMarkAsPaidDialog(p)
        },
        {
            label: PayableAction.VIEW_CHECK,
            icon: 'eye',
            condition: (p) => {
                const isLobCheck = p.currentFulfillment?.fulfillmentType === PayableFulfillmentType.FULFILLMENT_TYPE_LOB_BILL_PAYMENT &&
                        p.currentFulfillment?.fulfillmentReference?.previewUrl;
                const isPrintedCheck = p.currentFulfillment?.fulfillmentType === PayableFulfillmentType.FULFILLMENT_TYPE_PRINTED_CHECK &&
                        (p.currentFulfillment?.status === PayableState.CHECK_PRINTED || p.currentFulfillment?.status === PayableState.PAYMENT_MARKED_AS_PAID);

                return isLobCheck || isPrintedCheck;
            },
            action: (p) => this.viewCheck(p)
        },
        {
            label: PayableAction.TRACK_MAIL,
            icon: 'truck',
            condition: (p) => {
                return (p.currentFulfillment?.fulfillmentType === PayableFulfillmentType.FULFILLMENT_TYPE_LOB_BILL_PAYMENT) &&
                    p.currentFulfillment?.status !== PayableState.PENDING_APPROVAL &&
                    !p.deletedAt;
            },
            action: (p) => this.openTrackMailDialog(p)
        },
        {
            label: PayableAction.VIEW_INVOICE,
            icon: 'eye',
            condition: (p) => !!p.invoiceUploadedFileId,
            action: (p) => this.openViewInvoiceDialog(p)
        },
        {
            label: PayableAction.DOWNLOAD,
            icon: 'download',
            condition: (p) => !!p.invoiceUploadedFileId,
            action: (p) => this.downloadInvoice(p)
        },
        {
            label: PayableAction.EXPORT_ATTACHMENTS,
            icon: 'file-zip-o',
            condition: (p) => this.hasAttachments(p),
            action: (p) => this.exportPayableAttachments(p)
        },
        {
            // ACH is a true cancellation — the transfer is stopped, nothing is being marked
            // voided. Checks and marked-as-paid get the VOID entry below instead (SD-10649).
            label: PayableAction.CANCEL,
            icon: 'ban',
            textClass: 'text-danger',
            condition: (p) => this.canStopPayment(p) &&
                    p.currentFulfillment?.fulfillmentType === PayableFulfillmentType.FULFILLMENT_TYPE_TREASURY_ACH,
            action: (p) => this.cancelPaymentConfirmation(p)
        },
        {
            // Everything that is not an ACH transfer — mailed checks, printed checks and
            // marked-as-paid — is voided in PayHOA rather than stopped at the rail.
            label: PayableAction.VOID,
            icon: 'ban',
            textClass: 'text-danger',
            condition: (p) => this.canStopPayment(p) &&
                    p.currentFulfillment?.fulfillmentType !== PayableFulfillmentType.FULFILLMENT_TYPE_TREASURY_ACH,
            action: (p) => this.cancelPaymentConfirmation(p)
        },
        {
            label: PayableAction.RETURN_TO_DRAFT,
            icon: 'undo',
            iconClass: 'icon-return-draft',
            condition: (p) => this.hasWriteAccess() &&
                p.allowedTransitions.includes(PayableState.DRAFT) &&
                !p.deletedAt,
            action: (p) => this.startReturnPayableToDraftDialog(p)
        },
        {
            label: PayableAction.DELETE,
            icon: 'trash',
            iconClass: 'icon-delete',
            textClass: 'text-danger',
            condition: (p) => this.hasWriteAccess() && (
                            p.status === PayableState.DRAFT ||
                            p.currentFulfillment?.status === PayableState.PAYMENT_DECLINED ||
                            p.currentFulfillment?.status === PayableState.PAYMENT_CANCELLED
                        ) && !p.deletedAt,
            action: (p) => this.deletePayable(p)
        },
        {
            label: PayableAction.RESTORE,
            icon: 'restore',
            iconClass: 'icon-restore',
            condition: (p) => !!p.deletedAt,
            action: (p) => this.doRestorePayable(p)
        },
        {
            label: PayableAction.CLEAR_ERRORS,
            icon: 'super-star',
            textClass: 'text-danger',
            condition: (p) => this.isSuperUser() &&
                    p.currentFulfillment?.hasError,
            action: (p) => this.clearFulfillmentErrors(p)
        },
        {
            label: PayableAction.MARK_AS_PROCESSED,
            icon: 'super-star',
            condition: (p) => this.isSuperUser() &&
                    p.currentFulfillment?.status === PayableState.PAYMENT_PROCESSING &&
                    p.currentFulfillment?.fulfillmentType === PayableFulfillmentType.FULFILLMENT_TYPE_LOB_BILL_PAYMENT,
            action: (p) => this.markAsProcessed(p)
        },
        {
            label: PayableAction.AUDITS,
            icon: 'super-star',
            iconClass: 'icon-audits',
            condition: () => this.isSuperUser(),
            action: (p) => this.openAudits(p)
        }
    ];

    constructor(
        private _dialog: MatDialog,
        // MDC dialog for standalone V2 dialogs (legacy MatDialog can't provide MatMdcDialogData)
        private _mdcDialog: MatMdcDialog,
        private _router: Router,
        private _payablesService: PayablesService,
        private _growlerService: GrowlerService,
        private _createPayableDialog: CreatePayableDialogStateService,
        private _payableUpdateDialog: PayableUpdateDialogStateService,
        private _checkPrintingDialog: CheckPrintingDialogStateService,
        private _downloadCheckDialog: DownloadCheckDialogStateService,
    ) {}

    get actionWorking$() {
        return this.actionWorkingSubject$.asObservable();
    }

    get refreshAfterAction$() {
        return this.refreshAfterActionSubject$.asObservable();
    }

    get actionDone$() {
        return this.actionDoneSubject$.asObservable();
    }

    get allActions() {
        return this._allActions;
    }

    getAvailableActions(payable?: Payable): PayableActionConfig[] {
        if (!payable) {
            return [];
        }

        return this._allActions.filter(a => !a.condition || a.condition(payable));
    }

    isPendingApproval(payable: Payable): boolean {
        return payable.currentFulfillment?.status === PayableState.PENDING_APPROVAL;
    }

    viewCheck(payable: Payable): void {
        this._payablesService.downloadCheckImage(payable.id)
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: (blob: Blob) => {
                        const url = window.URL.createObjectURL(blob);
                        window.open(url, '_blank');

                        // Clean up the blob URL after a delay
                        setTimeout(() => window.URL.revokeObjectURL(url), 100);
                    },
                    error: () => {
                        this._growlerService.error('Error', 'Failed to load check image');
                    }
                });
    }

    viewPayableDetails(payable: Payable): void {
        this._router.navigate(['/app/payable/' + payable.id]);
    }

    openCreatePayableDialog(payable: Payable): Observable<any> {
        return this._createPayableDialog.ask({ payable: payable }).pipe(
            filter(result => result?.success),
            tap(() => this.refreshAfterActionSubject$.next()),
        );
    }

    openViewInvoiceDialog(payable: Payable): void {
        this._dialog.open(ViewInvoiceDialogComponent, {
            width: MatDialogSizes.LG,
            data: { payable: payable },
            panelClass: 'view-invoice-dialog-panel'
        });
    }

    openUpdatePayableDialog(payable: Payable): Observable<any> {
        return this.openUpdatePayableDialogById(payable.id);
    }

    openUpdatePayableDialogById(payableId: number): Observable<any> {
        return this._payableUpdateDialog.ask(payableId).pipe(
            filter(result => result?.success),
            tap(() => this.refreshAfterActionSubject$.next()),
        );
    }

    schedulePayment(payable: Payable): Observable<any> {
        const dialogRef = this._dialog.open(PayInvoiceDialogComponent, {
            width: MatDialogSizes.SM,
            panelClass: 'pay-invoice-dialog-panel',
            data: { payable: payable }
        });

        return dialogRef.afterClosed().pipe(
            filter(result => result?.success),
            tap(() => this.refreshAfterActionSubject$.next()),
        );
    }

    openPrintCheckDialog(payable: Payable): Observable<any> {
        const closed$: Observable<any> = payable.currentFulfillment?.fulfillmentType === PayableFulfillmentType.FULFILLMENT_TYPE_PRINTED_CHECK
                ? this._downloadCheckDialog.ask(payable)
                : this._checkPrintingDialog.ask(payable);

        return closed$.pipe(
            filter(result => {
                if (!result) { return false; }

                if (result.hasOwnProperty('success')) {
                    return (result as { success: boolean }).success;
                }

                return true;
            }),
            tap((result) => {
                if (result?.success) {
                    this.refreshAfterActionSubject$.next();
                }
            }),
        );
    }

    openTrackMailDialog(payable: Payable): void {
        this._dialog.open(TrackMailDialogComponent, {
            width: MatDialogSizes.SM,
            data: {
                title: `Track mailed check ${payable.invoiceNumber ? '• #' + payable.invoiceNumber : ''}`,
                payableId: payable.id
            }
        });
    }

    downloadInvoice(payable: Payable): void {
        this._payablesService.downloadPayableInvoice(payable.id).pipe(untilDestroyed(this)).subscribe({
            error: () => this._growlerService.oops('There was a problem downloading the invoice.'),
        });
    }

    openMarkAsPaidDialog(payable: Payable): Observable<any> {
        const dialogRef = this._dialog.open(MarkAsPaidDialogComponent, {
            width: MatDialogSizes.LG,
            panelClass: 'mark-as-paid-dialog-panel',
            data: {
                payableId: payable.id,
                vendorName: this.vendorNameOf(payable),
                invoiceTotal: payable.amount,
                paymentAmount: payable.paymentAmount || payable.amount,
                scheduledPaymentDate: payable.scheduledPaymentDate,
                invoiceNumber: payable.invoiceNumber,
                invoiceDate: payable.invoiceDate,
                dueDate: payable.dueDate,
                memo: payable.memo,
                expenseCategories: payable.expenseCategories,
                payable: payable
            }
        });

        return dialogRef.afterClosed().pipe(
            filter(result => result?.success),
            tap(() => this.refreshAfterActionSubject$.next()),
        );
    }

    startReturnPayableToDraftDialog(payable: Payable): Observable<any> {
        const dialogRef = this._dialog.open(ConfirmActionDialogComponent, {
            data: {
                title: 'Confirm payable returning to draft',
                content: `Are you sure you wish to return this payable to draft?<br><br>The payable will remain unpaid until it is scheduled for payment.`,
                confirmButton: 'Return payable to draft - do not pay'
            },
            width: MatDialogSizes.MD
        });

        return dialogRef.afterClosed().pipe(
            filter(result => result?.success),
            switchMap(() => this.returnToDraft(payable)),
        );
    }

    cancelPaymentConfirmation(payable: Payable): Observable<any> {
        const dialogRef = this._dialog.open(CancelPaymentDialogComponent, {
            width: MatDialogSizes.SM,
            data: { payable }
        });

        return dialogRef.afterClosed().pipe(
            filter(result => result?.success),
            switchMap((result) => this.cancelPayment(payable, result?.cancellationDate)),
        );
    }

    cancelPayment(payable: Payable, cancellationDate?: string): Observable<any> {
        this.actionWorkingSubject$.next();

        // Say back the same word the menu offered (SD-10649, simple-reviewer F-5819). Telling
        // someone who clicked Void that the payment was "cancelled" undoes the distinction the
        // labels were changed to draw. The dialog's own title and confirm button are still
        // cancel-worded; those belong to SD-10666 and are deliberately not touched here.
        const isVoid = payable.currentFulfillment?.fulfillmentType
            !== PayableFulfillmentType.FULFILLMENT_TYPE_TREASURY_ACH;
        const successMessage = isVoid
            ? 'This payment was voided.'
            : 'This payment was cancelled.';

        return this._payablesService.cancelPayment(payable.id, cancellationDate).pipe(
            tap(() => this._growlerService.success('Success', successMessage)),
            tap(() => this.refreshAfterActionSubject$.next()),
            catchError((error) => {
                const message = error?.error?.message
                    || (isVoid ? 'Unable to void this payment.' : 'Unable to cancel this payment.');
                this._growlerService.error('Error', message);
                return EMPTY;
            }),
            finalize(() => this.actionDoneSubject$.next()),
        );
    }

    deletePayable(payable: Payable): Observable<any> {
        const dialogRef = this._dialog.open(ConfirmActionDialogComponent, {
            width: MatDialogSizes.CONFIRM,
            data: {
                title: 'Delete payable?',
                content: 'Are you sure you wish to delete this payable' +
                    (payable.vendorId ? ' from ' + (payable.vendor?.name || 'Unknown Vendor') : '') + '?',
                confirmButton: 'Yes, delete it'
            }
        });

        return dialogRef.afterClosed().pipe(
            filter(result => result?.success),
            switchMap(() => this.doDeletePayable(payable)),
        );
    }

    openAudits(payable: Payable): void {
        this._mdcDialog.open(EntityHistoryDialogComponent, {
            width: MatDialogSizes.XL,
            maxWidth: '95vw',
            position: { top: '50px' },
            panelClass: 'entity-history-dialog',
            autoFocus: 'dialog',
            data: <EntityHistoryDialogInput>{
                subjectType: 'payable',
                subjectId: payable.id,
                subjectLabel: payable.invoiceNumber
                    ? `Payable #${payable.invoiceNumber}`
                    : `Payable #${payable.id}`,
            },
        });
    }

    clearFulfillmentErrors(payable: Payable): Observable<any> {
        const dialogRef = this._dialog.open(ConfirmActionDialogComponent, {
            width: MatDialogSizes.CONFIRM,
            data: {
                title: 'Clear fulfillment errors?',
                content: 'Are you sure you wish to clear all errors for this payable\'s fulfillment? This action will mark all error records as cleared with reason "manual_clear".',
                confirmButton: 'Yes, clear errors'
            }
        });

        return dialogRef.afterClosed().pipe(
            filter(result => result?.success),
            switchMap(() => this.doClearFulfillmentErrors(payable)),
        );
    }

    markAsProcessed(payable: Payable): Observable<any> {
        const dialogRef = this._dialog.open(ConfirmActionDialogComponent, {
            width: MatDialogSizes.CONFIRM,
            data: {
                title: 'Set status as processed?',
                content: 'Are you sure you wish to mark this mailed check as processed? This should only be used when USPS tracking has failed to update and you have confirmed the check was delivered.',
                confirmButton: 'Yes, mark as processed'
            }
        });

        return dialogRef.afterClosed().pipe(
            filter(result => result?.success),
            switchMap(() => this.doMarkAsProcessed(payable)),
        );
    }

    /** Same fallback as the payables table: a deleted vendor loads no relation, so use the payee name. */
    private vendorNameOf(payable: Payable): string {
        return payable.vendor?.name || payable.originalModel?.vendor?.name
            || payable.payeeName || payable.originalModel?.payeeName || '';
    }

    approveOrDenyPayable(payable: Payable, isApproved: boolean, payableApproverId: number): Observable<any> {
        const dialogRef = this._dialog.open(ApprovePayableDialogComponent, {
            width: MatDialogSizes.SM,
            data: {
                title: isApproved ? 'Approve payable' : 'Deny payable',
                buttonText: isApproved ? 'Approve' : 'Deny',
                payableId: payable.id,
                invoiceTotal: payable.amount,
                paymentAmount: payable.amount,
                scheduledPaymentDate: payable.scheduledPaymentDate,
                vendorName: this.vendorNameOf(payable),
            }
        });

        return dialogRef.afterClosed().pipe(
            filter(result => result?.success),
            switchMap(() => this.doUpdatePayableApproval(payable, isApproved, payableApproverId)),
        );
    }

    isCheckPayment(payable: Payable): boolean {
        const fulfillmentType = payable?.currentFulfillment?.fulfillmentType;
        return fulfillmentType === PayableFulfillmentType.FULFILLMENT_TYPE_LOB_BILL_PAYMENT ||
                fulfillmentType === PayableFulfillmentType.FULFILLMENT_TYPE_PRINTED_CHECK;
    }

    private returnToDraft(payable: Payable): Observable<any> {
        this.actionWorkingSubject$.next();
        return this._payablesService.returnToDraft(payable.id).pipe(
            tap(() => this._growlerService.success('Success', 'This payable has been converted to draft.')),
            tap(() => this.refreshAfterActionSubject$.next()),
            catchError((error) => {
                const message = error?.error?.message || 'Unable to return this payable to draft.';
                this._growlerService.error('Error', message);
                return EMPTY;
            }),
            finalize(() => this.actionDoneSubject$.next()),
        );
    }

    private doDeletePayable(payable: Payable): Observable<any> {
        this.actionWorkingSubject$.next();
        return this._payablesService.deletePayable(payable).pipe(
            tap(() => this._growlerService.success('Success', 'This payable was deleted.')),
            tap(() => this.refreshAfterActionSubject$.next()),
            catchError((error) => {
                const message = error?.error?.message || 'Unable to delete this payable.';
                this._growlerService.error('Error', message);
                return EMPTY;
            }),
            finalize(() => this.actionDoneSubject$.next()),
        );
    }

    private doRestorePayable(payable: Payable): Observable<any> {
        this.actionWorkingSubject$.next();
        return this._payablesService.restorePayables([payable.id]).pipe(
            tap(() => this._growlerService.success('Success', 'This payable was restored.')),
            tap(() => this.refreshAfterActionSubject$.next()),
            catchError((error) => {
                const message = error?.error?.message || 'Unable to restore this payable.';
                this._growlerService.error('Error', message);
                return EMPTY;
            }),
            finalize(() => this.actionDoneSubject$.next()),
        );
    }

    private doUpdatePayableApproval(payable: Payable, isApproved: boolean, payableApproverId: number): Observable<any> {
        // Find existing approval or create a new one
        let approval = payable.currentFulfillment?.approvals?.find(a =>
            a.payableApproverId === payableApproverId);

        if (!approval) {
            approval = new PayableApproval({
                organizationId: payable.organizationId,
                payableFulfillmentId: payable.currentFulfillment?.id,
                payableApprovalRuleId: payable.currentFulfillment?.approvalRule.id,
                payableApproverId: payableApproverId,
                approved: isApproved
            });
            approval.denied = !isApproved;
        } else {
            approval.approved = isApproved;
            approval.denied = !isApproved;
        }

        this.actionWorkingSubject$.next();
        return this._payablesService.setPayableApproval(approval).pipe(
            tap(() => {
                const payableDecision = isApproved ? 'approved' : 'denied';
                const totalApproved = payable.currentFulfillment?.approvals.filter(a => a.approved).length + (isApproved ? 1 : 0);
                const numRequired = payable.currentFulfillment?.approvalRule.numberRequired;
                const payableApprovedMessage = totalApproved >= numRequired
                    ? (payable.scheduledPaymentDate
                        ? 'The payable is now scheduled for payment'
                        : 'The payable can now be scheduled for payment')
                    : 'The payable requires additional approvals';

                this._growlerService.success('Success', `You ${payableDecision} the payable. ${payableApprovedMessage}.`);
            }),
            tap(() => this.refreshAfterActionSubject$.next()),
            catchError((error) => {
                const message = error?.error?.message || 'Unable to process this approval.';
                this._growlerService.error('Error', message);
                return EMPTY;
            }),
            finalize(() => this.actionDoneSubject$.next()),
        );
    }

    // Helper methods for conditions
    private hasWriteAccess(): boolean {
        return LegFiJwtService.doesUserHaveModulePermission('billpay', true);
    }

    private isSuperUser(): boolean {
        const jwt = LegFiJwtService.read();
        return jwt?.superUser === true;
    }

    /**
     * Shared gate for the CANCEL and VOID actions — everything they have in common. Which of the
     * two is offered is then decided by the fulfillment type alone, so exactly one can ever show.
     *
     * Payabli / Vendor Decide is excluded outright: cancelling one marks the fulfillment Cancelled
     * here but never tells Payabli to stop the payment, so the vendor is still paid (SD-10649).
     *
     * Note this tests `fulfillmentType`, NOT `paymentMethodType`. The API sends the human display
     * label in `paymentMethodType` ('Vendor Decide Payment', via
     * PayableFulfillment::FULFILLMENT_TYPE_DESCRIPTIONS), never the 'vendorDecide' request-side
     * code — so the previous comparison against PaymentMethod.VENDOR_DECIDE could never match and
     * the action showed on every Payabli payable. `fulfillmentType` carries the raw value
     * ('payabli') and is what the sibling conditions in this file already use.
     */
    private canStopPayment(payable: Payable): boolean {
        return this.hasWriteAccess()
            && payable.allowedTransitions.includes(PayableState.PAYMENT_CANCELLED)
            && payable.currentFulfillment?.fulfillmentType !== PayableFulfillmentType.FULFILLMENT_TYPE_PAYABLI_BILL_PAYMENT;
    }

    hasAttachments(payable: Payable): boolean {
        // Check if has invoice file or other attachments
        return !!payable.invoiceUploadedFileId || (payable.attachments && payable.attachments.length > 0);
    }

    exportPayableAttachments(payable: Payable): void {
        this._payablesService.exportPayableAttachments(payable.id).pipe(untilDestroyed(this)).subscribe({
            next: (blob: Blob) => {
                // Create download link and trigger download
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = this.generateZipFileName(payable);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                this._growlerService.success('Export Complete', 'Successfully exported attachments for payable.');
            },
            error: () => {
                this._growlerService.oops('There was a problem exporting the attachments.');
            }
        });
    }

    private generateZipFileName(payable: Payable): string {
        const sanitize = (name: string): string => {
            return name.replace(/[^A-Za-z0-9]/g, '') || 'Unknown';
        };

        const vendorName = sanitize(payable.vendor?.name ?? 'UnknownVendor');
        const invoiceDate = payable.invoiceDate ?
            (moment.isMoment(payable.invoiceDate) ?
                payable.invoiceDate.format('YYYYMMDD') :
                moment(payable.invoiceDate).format('YYYYMMDD')
            ) :
            moment().format('YYYYMMDD');
        const invoiceNumber = sanitize(payable.invoiceNumber ?? 'noInvNum');
        const amount = Math.round((payable.amount ?? 0) / 100).toString();

        return `${vendorName}_${invoiceDate}_${invoiceNumber}_${amount}.zip`;
    }

    private doClearFulfillmentErrors(payable: Payable): Observable<any> {
        this.actionWorkingSubject$.next();
        return this._payablesService.clearFulfillmentErrors(payable.id).pipe(
            tap(() => this._growlerService.success('Success', 'All fulfillment errors have been cleared.')),
            tap(() => this.refreshAfterActionSubject$.next()),
            catchError((error) => {
                const message = error?.error?.message || 'Unable to clear fulfillment errors.';
                this._growlerService.error('Error', message);
                return EMPTY;
            }),
            finalize(() => this.actionDoneSubject$.next()),
        );
    }

    private doMarkAsProcessed(payable: Payable): Observable<any> {
        this.actionWorkingSubject$.next();
        return this._payablesService.markAsProcessed(payable.id).pipe(
            tap(() => this._growlerService.success('Success', 'Payable has been marked as processed.')),
            tap(() => this.refreshAfterActionSubject$.next()),
            catchError((error) => {
                const message = error?.error?.message || 'Unable to mark payable as processed.';
                this._growlerService.error('Error', message);
                return EMPTY;
            }),
            finalize(() => this.actionDoneSubject$.next()),
        );
    }
}
